import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DollarSign, Lock, Package, AlertCircle } from 'lucide-react'
import { ValueCard, InfoBox } from '../../ui'
import { StepCard, PriceFieldCard, CheckboxField, SelectField, SelectOption } from '../../form'
import { formatCurrency, parseCurrency } from '../../../utils/formatters'
import { cn } from '@/lib/utils'

interface VehicleOptionItem {
  value: string
  label: string
}

interface VehicleEditStep2PricingProps {
  formData: {
    price: string
    minimumPrice: string
    purchasePrice: string
    hidePricing: boolean
    status: string
  }
  errors: Record<string, string>
  vehicleOptions: {
    statuses?: VehicleOptionItem[]
  }
  onInputChange: (field: string, value: string | boolean) => void
}

export const VehicleEditStep2Pricing: React.FC<VehicleEditStep2PricingProps> = ({
  formData,
  errors,
  vehicleOptions,
  onInputChange,
}) => {
  const { t } = useTranslation()
  const calculateDiff = () => {
    if (!formData.price || !formData.purchasePrice) return null
    const sale = parseCurrency(formData.price)
    const purchase = parseCurrency(formData.purchasePrice)
    if (purchase === 0 && sale === 0) return null
    const diff = sale - purchase
    const pct = purchase > 0 ? (diff / purchase) * 100 : 0
    return { value: diff, percentage: pct, formatted: formatCurrency(diff) }
  }

  const calculateMinDiff = () => {
    if (!formData.minimumPrice || !formData.purchasePrice) return null
    const minPrice = parseCurrency(formData.minimumPrice)
    const purchase = parseCurrency(formData.purchasePrice)
    if (purchase === 0 && minPrice === 0) return null
    const diff = minPrice - purchase
    const pct = purchase > 0 ? (diff / purchase) * 100 : 0
    return { value: diff, percentage: pct, formatted: formatCurrency(diff) }
  }

  const minPriceWarning = useMemo(() => {
    if (!formData.price || !formData.minimumPrice) return false
    const sale = parseCurrency(formData.price)
    const min = parseCurrency(formData.minimumPrice)
    return sale > 0 && min > sale
  }, [formData.minimumPrice, formData.price])

  const statusOptions: SelectOption[] = useMemo(
    () => (vehicleOptions?.statuses || []).map((status) => ({ value: status.value, label: status.label })),
    [vehicleOptions],
  )

  const diff = calculateDiff()
  const minDiff = calculateMinDiff()

  return (
    <>
      <StepCard title={t('vehicleRegistration.pricing.title')} icon={DollarSign} description={t('vehicleRegistration.pricing.description')}>
        <InfoBox variant="info">{t('vehicleRegistration.pricing.manualInfo')}</InfoBox>

        <PriceFieldCard
          title={t('vehicleRegistration.pricing.salePriceTitle')}
          icon={DollarSign}
          value={formData.price}
          onChange={(value) => onInputChange('price', value)}
          error={errors.price}
          variant="sale"
          badgeLabel={t('vehicleRegistration.pricing.requiredBadge')}
          showMarginButtons={false}
        />

        <PriceFieldCard
          title={t('vehicleRegistration.pricing.minimumPriceTitle')}
          icon={Lock}
          value={formData.minimumPrice}
          onChange={(value) => onInputChange('minimumPrice', value)}
          error={errors.minimumPrice}
          variant="confidential"
          badgeLabel={t('vehicleRegistration.pricing.confidentialBadge')}
          showMarginButtons={false}
          infoTitle={t('vehicleRegistration.pricing.confidentialInfoTitle')}
          infoMessage={t('vehicleRegistration.pricing.confidentialInfoMessage')}
        />

        {minPriceWarning && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {t('vehicleRegistration.pricing.minPriceWarning')}
          </div>
        )}

        <PriceFieldCard
          title={t('vehicleRegistration.pricing.purchasePriceTitle')}
          icon={Lock}
          value={formData.purchasePrice}
          onChange={(value) => onInputChange('purchasePrice', value)}
          error={errors.purchasePrice}
          variant="confidential"
          badgeLabel={t('vehicleRegistration.pricing.confidentialBadge')}
          infoTitle={t('vehicleRegistration.pricing.confidentialInfoTitle')}
          infoMessage={t('vehicleRegistration.pricing.purchasePriceInfoMessage')}
          showMarginButtons={false}
        />

        {(diff || minDiff) && (
          <div className={cn('grid gap-3', diff && minDiff ? 'grid-cols-2' : 'grid-cols-1')}>
            {diff && (
              <ValueCard
                title={t('vehicleRegistration.pricing.diffTitle')}
                value={diff.formatted}
                color={diff.value >= 0 ? 'success' : 'danger'}
                badge={`${diff.percentage >= 0 ? '+' : ''}${diff.percentage.toFixed(1)}%`}
                subtitle={t('vehicleRegistration.pricing.diffSubtitle')}
              />
            )}
            {minDiff && (
              <ValueCard
                title={t('vehicleRegistration.pricing.minDiffTitle')}
                value={minDiff.formatted}
                color={minDiff.value >= 0 ? 'success' : 'danger'}
                badge={`${minDiff.percentage >= 0 ? '+' : ''}${minDiff.percentage.toFixed(1)}%`}
                subtitle={t('vehicleRegistration.pricing.minDiffSubtitle')}
              />
            )}
          </div>
        )}

        <CheckboxField
          label={t('vehicleRegistration.pricing.hidePricingLabel')}
          description={t('vehicleRegistration.pricing.hidePricingDescription')}
          checked={formData.hidePricing}
          onChange={(checked) => onInputChange('hidePricing', checked)}
          activeBadge={t('vehicleRegistration.pricing.activeBadge')}
        />
      </StepCard>

      <StepCard
        title={t('vehicleRegistration.pricing.statusTitle')}
        icon={Package}
        description={t('vehicleRegistration.pricing.statusDescription')}
      >
        <SelectField
          label={t('vehicleRegistration.pricing.statusLabel')}
          value={formData.status}
          options={statusOptions}
          onChange={(value) => onInputChange('status', value)}
          placeholder={t('vehicleRegistration.pricing.statusPlaceholder')}
          required
          error={errors.status}
        />
      </StepCard>
    </>
  )
}
